import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useCurrencyRail } from "@/context/CurrencyRailContext";
import ConsolidatedRailCard from "@/components/ConsolidatedRailCard";
import CashForecastCard from "@/components/CashForecastCard";
import SavedDrillsCard from "@/components/SavedDrillsCard";
import { formatCurrency, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import MultiTransactionForm from "@/components/transactions/MultiTransactionForm";
import {
  ArrowDownRight, ArrowUpRight, PiggyBank, Sparkles, Plus, TrendingUp, AlertTriangle,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import OnboardingWizard from "@/components/OnboardingWizard";

function StatCard({ title, value, delta, icon: Icon, tint }) {
  return (
    <div className="card-flat p-6 hover-lift">
      <div className="flex items-start justify-between">
        <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">{title}</div>
        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${tint}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="mt-4 font-mono-data text-3xl font-semibold tracking-tight">
        {value}
      </div>
      {delta && <div className="mt-1 text-xs text-[var(--muted-text)]">{delta}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const currency = user?.currency || "USD";
  const { displayAmount } = useCurrency() || {};
  const { filterRails, activeRail } = useCurrencyRail();
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [nudges, setNudges] = useState([]);
  // Pre-computed groups: keeps the JSX below cheap and avoids re-filtering the
  // same list four times per render.
  const overNudges = useMemo(() => nudges.filter((n) => n.status === "over"), [nudges]);
  const warnNudges = useMemo(() => nudges.filter((n) => n.status === "warn"), [nudges]);
  const overCatsText = useMemo(() => overNudges.map((n) => n.category).join(", "), [overNudges]);
  const warnCatsText = useMemo(() => warnNudges.map((n) => n.category).join(", "), [warnNudges]);
  const [insights, setInsights] = useState("");
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [insightsError, setInsightsError] = useState("");
  const [open, setOpen] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [{ data: sumData }, { data: txData }, { data: prog }] = await Promise.all([
        api.get("/analytics/summary"),
        api.get("/transactions?limit=6"),
        api.get("/budgets/progress"),
      ]);
      setSummary(sumData);
      setRecent(txData);
      setNudges((prog.budgets || []).filter((b) => b.status !== "ok"));
    } catch (err) {
      console.warn("[Dashboard] loadAll failed:", err?.response?.status, err?.message);
    }
  }, []);

  useEffect(() => {
    // Load data immediately so the dashboard isn't empty while background jobs run.
    loadAll();
    // Retry once after 1.5s to catch any just-seeded data (e.g. fresh demo user)
    const t = setTimeout(loadAll, 1500);
    (async () => {
      try { await api.post("/recurring/run"); }
      catch (err) { console.debug("[Dashboard] recurring/run:", err?.message); }
      try { await api.post("/notifications/check"); }
      catch (err) { console.debug("[Dashboard] notifications/check:", err?.message); }
      try { await api.get("/investments/net-worth?snapshot=true"); }
      catch (err) { console.debug("[Dashboard] net-worth snapshot:", err?.message); }
      try { await api.post("/insights/weekly-digest?auto=true"); }
      catch (err) { console.debug("[Dashboard] weekly-digest:", err?.message); }
      loadAll();
    })();
    if (user && user.onboarding_completed === false && !user.is_demo) {
      setOnboardingOpen(true);
    }
    return () => clearTimeout(t);
  }, [user, loadAll]);

  const trend = useMemo(() => {
    if (!summary) return [];
    return (summary.monthly_trend || []).map((m) => ({
      month: m.month,
      income: m.income,
      expense: m.expense,
    }));
  }, [summary]);

  const generateInsights = async () => {
    setLoadingInsights(true);
    setInsightsError("");
    try {
      const { data } = await api.post("/insights/generate");
      setInsights(data.insights || "");
    } catch (e) {
      setInsightsError(e.response?.data?.detail || "Unable to generate insights.");
    } finally {
      setLoadingInsights(false);
    }
  };

  return (
    <AppShell>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Overview</div>
          <h1 className="font-display text-4xl mt-2">Hello, {user?.name?.split(" ")[0]}</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-transaction-btn" className="rounded-md btn-brand h-11 px-5">
              <Plus className="w-4 h-4 mr-2" /> Add transaction
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="add-transaction-dialog" className="w-[96vw] max-w-[1240px] sm:max-w-[1240px]">
            <DialogHeader><DialogTitle>New transactions</DialogTitle>
<DialogDescription className="text-xs">Enter one or more lines, then save them together.</DialogDescription></DialogHeader>
            <MultiTransactionForm
              currency={currency}
              onDone={() => { setOpen(false); loadAll(); }}
              onCancel={() => setOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </div>

      {nudges.length > 0 && (
        <div data-testid="dashboard-nudges" className="card-flat p-4 mb-4 border-l-4 border-l-[#D4A017] flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-[#D4A017] mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Heads-up</div>
            <div className="text-sm mt-1">
              {overNudges.length > 0 && (
                <span data-testid="nudge-over">
                  Over budget in{" "}
                  <span className="font-medium text-[#E63946]">
                    {overCatsText}
                  </span>
                  .{" "}
                </span>
              )}
              {warnNudges.length > 0 && (
                <span data-testid="nudge-warn">
                  Close to limit in{" "}
                  <span className="font-medium text-[#D4A017]">
                    {warnCatsText}
                  </span>
                  .
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {(() => {
        const rails = filterRails(summary?.by_currency || []);
        const showMixed = activeRail === "ALL" && (!Array.isArray(summary?.by_currency) || summary.by_currency.length <= 1);
        return (
          <>
            {showMixed && (
              <div data-testid="dashboard-stats" className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <StatCard title="Balance" value={formatCurrency(summary?.balance ?? 0, currency)}
                  delta="Income − expenses − savings" icon={TrendingUp} tint="bg-[#2F4F4F] text-[#FDFCF8]" />
                <StatCard title="Income" value={formatCurrency(summary?.totals?.income ?? 0, currency)}
                  icon={ArrowUpRight} tint="bg-[#EAF4EE] text-[#4C956C]" />
                <StatCard title="Expenses" value={formatCurrency(summary?.totals?.expense ?? 0, currency)}
                  icon={ArrowDownRight} tint="bg-[#FCE7E9] text-[#E63946]" />
                <StatCard title="Savings" value={formatCurrency(summary?.totals?.saving ?? 0, currency)}
                  icon={PiggyBank} tint="bg-[#F5EAE3] text-[#E07A5F]" />
              </div>
            )}

            {/* Iter 68 — 30-day cash forecast widget */}
            <div className="mt-3">
              <CashForecastCard />
            </div>

            {/* Iter 72p — Saved Drill shortcuts widget */}
            <div className="mt-3">
              <SavedDrillsCard />
            </div>

            {/* Per-currency mini-cards — respects the sidebar rail filter */}
            {rails.length > 0 && (activeRail !== "ALL" || rails.length > 1) && (
              <>
                {/* FX-consolidated card (iter 60k) — shows only when sidebar
                    "Convert to USD" toggle is ON AND 2+ rails have activity.
                    Sums income + expenses + savings across USD + NGN via live FX. */}
                <div className="mt-3">
                  <ConsolidatedRailCard
                    rails={summary?.by_currency || []}
                    testId="dashboard-consolidated"
                    subtitle="income + expenses + savings across every rail"
                    fields={[
                      { key: "income", label: "Income", color: "text-[#4C956C]" },
                      { key: "expense", label: "Expenses", color: "text-[#E63946]" },
                      { key: "saving", label: "Savings", color: "text-[#E07A5F]" },
                    ]}
                  />
                </div>
                <div data-testid="dashboard-by-currency" className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {rails.map((r) => (
                  <div key={r.currency} data-testid={`dashboard-ccy-${r.currency}`}
                       className="card-flat p-5 border-l-4"
                       style={{ borderLeftColor: r.currency === "USD" ? "#1E5A96" : "#4C956C" }}>
                    <div className="flex items-baseline justify-between">
                      <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">{r.currency} rail</div>
                      <div className={`text-xs font-mono-data ${r.balance >= 0 ? "text-[#2F6B4A]" : "text-[#B4283A]"}`}>
                        Balance {formatCurrency(r.balance, r.currency)}
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-3">
                      <div>
                        <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">Income</div>
                        <div className="font-mono-data text-base mt-1 text-[#4C956C]">{formatCurrency(r.income, r.currency)}</div>
                      </div>
                      <div>
                        <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">Expenses</div>
                        <div className="font-mono-data text-base mt-1 text-[#E63946]">{formatCurrency(r.expense, r.currency)}</div>
                      </div>
                      <div>
                        <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">Savings</div>
                        <div className="font-mono-data text-base mt-1 text-[#E07A5F]">{formatCurrency(r.saving, r.currency)}</div>
                      </div>
                    </div>
                  </div>
                ))}
                </div>
              </>
            )}
          </>
        );
      })()}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-2 card-flat p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Monthly trend</div>
              <h3 className="font-display text-xl mt-1">Income vs expenses</h3>
            </div>
          </div>
          <div className="h-64" data-testid="dashboard-line-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="2 4" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "var(--muted-text)" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "var(--muted-text)" }} axisLine={false} tickLine={false} width={45} />
                <Tooltip contentStyle={{ border: "1px solid var(--border)", borderRadius: 8, background: "var(--surface)", color: "var(--text)" }} />
                <Line type="monotone" dataKey="income" stroke="#4C956C" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="expense" stroke="#E63946" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div
          data-testid="ai-insights-card"
          className="card-flat p-6 relative overflow-hidden"
          style={{ background: "linear-gradient(180deg, var(--ai-tint) 0%, var(--surface) 60%)" }}
        >
          <div className="flex items-center gap-2 text-[var(--brand)]">
            <Sparkles className="w-4 h-4" />
            <div className="text-xs tracking-[0.2em] uppercase">AI insights</div>
          </div>
          <h3 className="font-display text-xl mt-2">What your money says</h3>
          {insights ? (
            <p data-testid="ai-insights-text" className="mt-4 text-sm leading-relaxed whitespace-pre-wrap">
              {insights}
            </p>
          ) : (
            <p className="mt-4 text-sm text-[var(--muted-text)]">
              Get a concise take on your spending patterns and a smart next step.
            </p>
          )}
          {insightsError && <p className="mt-2 text-xs text-[#E63946]">{insightsError}</p>}
          <Button
            data-testid="generate-insights-btn"
            onClick={generateInsights}
            disabled={loadingInsights}
            className="mt-4 rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]"
          >
            {loadingInsights ? "Analyzing..." : insights ? "Regenerate" : "Generate insights"}
          </Button>
        </div>
      </div>

      <div className="card-flat p-6 mt-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Latest</div>
            <h3 className="font-display text-xl mt-1">Recent transactions</h3>
          </div>
        </div>
        <div data-testid="recent-transactions" className="divide-y divide-[var(--divider)]">
          {recent.length === 0 && (
            <div className="text-sm text-[var(--muted-text)] py-8 text-center">
              No transactions yet — add one to get started.
            </div>
          )}
          {recent.map((tx) => (
            <div key={tx.id} className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-md flex items-center justify-center ${
                  tx.type === "income" ? "bg-[#EAF4EE] text-[#4C956C]" :
                  tx.type === "expense" ? "bg-[#FCE7E9] text-[#E63946]" :
                  "bg-[#F5EAE3] text-[#E07A5F]"
                }`}>
                  {tx.type === "income" ? <ArrowUpRight className="w-4 h-4" /> :
                   tx.type === "expense" ? <ArrowDownRight className="w-4 h-4" /> :
                   <PiggyBank className="w-4 h-4" />}
                </div>
                <div>
                  <div className="text-sm font-medium">{tx.category}</div>
                  <div className="text-xs text-[var(--muted-text)]">{tx.note || formatDate(tx.date)}</div>
                </div>
              </div>
              <div className={`font-mono-data text-sm ${
                tx.type === "income" ? "text-[#4C956C]" : ""
              }`}>
                {tx.type === "income" ? "+" : "−"}{displayAmount ? displayAmount(tx.amount, tx.currency) : formatCurrency(tx.amount, tx.currency)}
              </div>
            </div>
          ))}
        </div>
      </div>

      <OnboardingWizard open={onboardingOpen} onClose={() => setOnboardingOpen(false)} />
    </AppShell>
  );
}
